/**
 * NewsPulseOpinion — Opinion & Analysis wall.
 * Kept visually distinct from news via a stark "OPINION" ribbon and a
 * warm palette so readers never confuse editorial with straight news.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MessageSquare, Feather, ScrollText, BookOpen, Info } from "lucide-react";
import { npApi } from "./api";
import EventCard from "./EventCard";

const SUBTOPICS = [
  ["all",                     "All Opinion"],
  ["editorials",              "Editorials"],
  ["columns",                 "Columns"],
  ["guest_essays",            "Guest Essays"],
  ["letters_to_the_editor",   "Letters"],
  ["political_analysis",      "Political Analysis"],
  ["economic_analysis",       "Economic Analysis"],
  ["cultural_commentary",     "Cultural"],
  ["debate",                  "Debate"],
];

export default function NewsPulseOpinion() {
  const { subtopic } = useParams();
  const active = subtopic || "all";
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = active === "all" ? { limit: 40 } : { subtopic: active, limit: 40 };
    npApi
      .opinion(params)
      .then((r) => { if (!cancelled) setFeed(r || []); })
      .catch(() => { if (!cancelled) setFeed([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [active]);

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-8" data-testid="newspulse-opinion">
      {/* Distinctive Opinion ribbon — never mistake this for news */}
      <section className="rounded-2xl bg-gradient-to-br from-amber-900 via-orange-800 to-red-800 p-8 md:p-12 text-white mb-2 relative overflow-hidden border-4 border-amber-700">
        <div className="absolute -right-6 -bottom-10 opacity-10">
          <Feather className="w-56 h-56" strokeWidth={0.6} />
        </div>
        <div className="relative">
          <div className="inline-flex items-center gap-2 mb-3 rounded-full bg-white/15 backdrop-blur-sm text-white text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1 border border-white/30">
            <MessageSquare className="w-3 h-3" /> Opinion & Analysis
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight max-w-4xl" data-testid="opinion-title">
            Perspectives, not news.
          </h1>
          <p className="mt-3 text-amber-100 max-w-2xl">
            Every piece on this page is an editorial, column, essay or analysis —
            the views of the writer, not TheNewsPulse's reporting. We keep opinion
            separate so factual news isn't diluted by commentary.
          </p>
        </div>
      </section>

      {/* Persistent "you are reading opinion" strip */}
      <div className="flex items-center gap-2 rounded-b-xl bg-amber-100 border-x-4 border-b-4 border-amber-700 text-amber-900 text-xs font-semibold px-4 py-2 mb-8">
        <Info className="w-3.5 h-3.5" />
        Opinion pieces are the writer's views, not TheNewsPulse reporting.
      </div>

      {/* Subtopic tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="opinion-tabs">
        {SUBTOPICS.map(([slug, label]) => (
          <Link
            key={slug}
            to={slug === "all" ? "/opinion" : `/opinion/${slug}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
              active === slug
                ? "bg-amber-900 text-white border border-amber-900"
                : "bg-white text-amber-900 border border-amber-300 hover:border-amber-700"
            }`}
            data-testid={`opinion-tab-${slug}`}
          >
            {label}
          </Link>
        ))}
      </div>

      {loading ? (
        <p className="text-slate-500">Loading opinion…</p>
      ) : feed.length ? (
        <>
          <p className="text-xs text-slate-500 mb-3">
            {feed.length} piece{feed.length === 1 ? "" : "s"} · latest first
          </p>
          <div data-testid="opinion-feed">
            {feed.map((e) => (
              <div key={e.event_id} className="relative">
                {/* Discreet "OPINION" corner tag on each card so a reader
                    scrolling fast still sees the label */}
                <div className="absolute z-10 -top-1 -left-1 inline-flex items-center gap-1 rounded-md bg-amber-800 text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5">
                  <ScrollText className="w-2.5 h-2.5" /> Opinion
                </div>
                <EventCard event={e} />
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
          <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500 italic">
            No opinion pieces here yet. The ingestion pipeline is picking them up
            from Guardian Opinion, The Atlantic Ideas, and more.
          </p>
        </div>
      )}
    </div>
  );
}
